import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Scale, Home, Users, FileText, Shield, Briefcase } from 'lucide-react';

const areas = [
  {
    icon: Users,
    title: 'Family Law',
    desc: 'Compassionate legal guidance through custody, support, and family-related matters with sensitivity and strength.',
  },
  {
    icon: Scale,
    title: 'Matrimonial Law',
    desc: 'Expert representation in divorce proceedings, prenuptial agreements, and marital dispute resolution.',
  },
  {
    icon: FileText,
    title: 'Estate Law',
    desc: 'Comprehensive estate planning, wills, trusts, and probate administration to protect your legacy.',
  },
  {
    icon: Briefcase,
    title: 'Corporate Disputes',
    desc: 'Strategic resolution of business conflicts, partnership disputes, and commercial litigation.',
  },
  {
    icon: Shield,
    title: 'Personal Injury',
    desc: 'Aggressive pursuit of fair compensation for injuries caused by negligence or wrongful conduct.',
  },
  {
    icon: Home,
    title: 'Real Estate',
    desc: 'Thorough legal support for property transactions, zoning issues, and real estate litigation.',
  },
];

function AreaCard({ area, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const Icon = area.icon;

  return (
    <motion.div
      ref={ref}
      data-testid={`practice-area-${area.title.toLowerCase().replace(/\s/g, '-')}`}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1, ease: 'easeOut' }}
      className="group relative p-8 rounded-lg border transition-all duration-500 cursor-pointer"
      style={{
        background: 'var(--color-surface)',
        borderColor: 'var(--color-border)',
      }}
      whileHover={{
        y: -4,
        boxShadow: '0 0 30px rgba(6, 182, 212, 0.2)',
        borderColor: 'var(--color-accent)',
      }}
    >
      <div
        className="w-12 h-12 rounded-lg flex items-center justify-center mb-5 transition-all duration-500 group-hover:shadow-[0_0_20px_rgba(6,182,212,0.3)]"
        style={{ background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))', color: '#fff' }}
      >
        <Icon size={22} />
      </div>
      <h3 className="font-heading font-bold text-xl mb-3 tracking-tight" style={{ color: 'var(--color-text)' }}>
        {area.title}
      </h3>
      <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
        {area.desc}
      </p>
      <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: 'linear-gradient(90deg, var(--color-secondary), var(--color-accent))' }}
      />
    </motion.div>
  );
}

export default function PracticeAreas() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="practice-areas" data-testid="practice-areas-section" className="py-24 md:py-32" style={{ background: 'var(--color-bg)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-4"
            style={{ background: 'var(--color-glow)', color: 'var(--color-accent)' }}
          >
            Our Expertise
          </span>
          <h2 data-testid="practice-areas-heading" className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-4" style={{ color: 'var(--color-text)' }}>
            Areas of Practice
          </h2>
          <p className="max-w-2xl mx-auto font-body text-lg leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
            We are dedicated law practitioners passionate about serving our community and
            improving relations between individuals, businesses, government and various other third parties.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {areas.map((area, i) => (
            <AreaCard key={area.title} area={area} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
